/**
 * Pre-orders
 *
 * - Shows a confirmation pop-up before a pre-order product is added to the cart,
 *   making clear whether the customer is paying an advance or the full price.
 * - Tags the cart line with a "Payment" property (shown on the order) and a hidden
 *   "_preorder" property used to recognise pre-order lines in the cart.
 * - Keeps pre-orders and in-stock items in separate checkouts.
 *
 * Product data comes from snippets/preorder-data.liquid (rendered inside product forms).
 * Text and settings come from snippets/preorder.liquid (Theme settings > Pre-orders).
 */
(() => {
  const configElement = document.getElementById('preorder-config');
  const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById('preorder-dialog'));
  if (!configElement || !dialog) return;

  const config = JSON.parse(configElement.textContent || '{}');
  const cartUrl = (typeof Theme !== 'undefined' && Theme.routes?.cart_url) || '/cart';
  const cartUpdateUrl = (typeof Theme !== 'undefined' && Theme.routes?.cart_update_url) || '/cart/update';

  /** @type {HTMLFormElement | null} Form that is currently allowed through after confirmation */
  let passForm = null;
  let dialogOpen = false;

  /**
   * @param {string} text
   */
  function escapeHtml(text) {
    return text.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
  }

  /**
   * Turns a settings message into paragraphs, filling in the placeholders.
   * @param {string} template
   * @param {Record<string, string>} values
   */
  function renderMessage(template, values) {
    let text = escapeHtml(template || '');
    for (const [key, value] of Object.entries(values)) {
      text = text.split(`[${key}]`).join(escapeHtml(value));
    }
    return text
      .split(/\n\s*\n/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean)
      .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
      .join('');
  }

  /**
   * Returns pre-order details for the variant currently selected in a product form, or null.
   * @param {HTMLFormElement | null | undefined} form
   */
  function getPreorderInfo(form) {
    const dataElement = form?.querySelector('script[data-preorder]');
    if (!form || !dataElement) return null;

    try {
      const data = JSON.parse(dataElement.textContent || '');
      const variantId = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'))?.value;
      const variant = variantId ? data.variants[variantId] : null;
      return variant ? { product: data.product, release: data.release, variant } : null;
    } catch (error) {
      console.error('Pre-order data could not be read', error);
      return null;
    }
  }

  async function fetchCart() {
    const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  }

  /**
   * @param {{ properties?: Record<string, string> | null }} item
   */
  function isPreorderItem(item) {
    return Boolean(item.properties && item.properties._preorder);
  }

  /**
   * Adds the line item properties for the selected option to the form.
   * @param {HTMLFormElement} form
   * @param {{ advance: boolean, balance: string }} variant
   */
  function setLineItemProperties(form, variant) {
    form.querySelectorAll('[data-preorder-property]').forEach((input) => input.remove());

    const properties = {
      [config.paymentLabel]: variant.advance
        ? config.advanceProperty.split('[balance]').join(variant.balance)
        : config.fullProperty,
      _preorder: variant.advance ? 'advance' : 'full',
    };

    for (const [name, value] of Object.entries(properties)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = value;
      input.dataset.preorderProperty = '';
      form.appendChild(input);
    }
  }

  /**
   * Opens the pop-up and resolves with the button the customer chose.
   * @param {ReturnType<typeof getPreorderInfo> & {}} info
   * @param {boolean} hasOtherItems
   * @returns {Promise<boolean>}
   */
  function askForConfirmation(info, hasOtherItems) {
    const { variant } = info;
    const values = {
      advance: variant.price,
      balance: variant.balance,
      total: variant.total,
      days: String(config.days),
      release: info.release ? ` (expected ${info.release})` : '',
    };

    dialog.querySelector('.preorder-dialog__title').textContent = variant.advance
      ? config.advanceTitle
      : config.fullTitle;
    dialog.querySelector('.preorder-dialog__product').textContent = info.product;
    dialog.querySelector('.preorder-dialog__body').innerHTML = renderMessage(
      variant.advance ? config.advanceText : config.fullText,
      values
    );

    const warning = /** @type {HTMLElement} */ (dialog.querySelector('.preorder-dialog__warning'));
    warning.textContent = config.mixedWarning;
    warning.hidden = !hasOtherItems;

    dialog.querySelector('.preorder-dialog__confirm').textContent = hasOtherItems ? config.clearAndAdd : config.confirm;
    dialog.querySelector('.preorder-dialog__cancel').textContent = config.cancel;

    dialog.returnValue = '';
    dialogOpen = true;
    dialog.showModal();

    return new Promise((resolve) => {
      dialog.addEventListener(
        'close',
        () => {
          dialogOpen = false;
          resolve(dialog.returnValue === 'confirm');
        },
        { once: true }
      );
    });
  }

  /**
   * @param {HTMLFormElement} form
   * @param {HTMLButtonElement | null} button
   */
  async function handlePreorderAdd(form, button) {
    const info = getPreorderInfo(form);
    if (!info || dialogOpen) return;

    let otherItemKeys = [];
    try {
      const cart = await fetchCart();
      otherItemKeys = cart.items.filter((item) => !isPreorderItem(item)).map((item) => item.key);
    } catch (error) {
      console.error('Could not check the cart', error);
    }

    const confirmed = await askForConfirmation(info, otherItemKeys.length > 0);

    if (!confirmed) {
      // The sticky add-to-cart bar marks the main button as a "puppet"; reset it when cancelled.
      if (button?.dataset.puppet === 'true') button.dataset.puppet = 'false';
      return;
    }

    if (otherItemKeys.length > 0) {
      const updates = Object.fromEntries(otherItemKeys.map((key) => [key, 0]));
      await fetch(`${cartUpdateUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
    }

    setLineItemProperties(form, info.variant);

    // Re-run the theme's normal add to cart, letting it past our interception once.
    passForm = form;
    try {
      if (button && form.contains(button)) {
        button.click();
      } else {
        form.requestSubmit();
      }
    } finally {
      passForm = null;
    }
  }

  /**
   * Stops the theme's add to cart for pre-order products until the customer confirms.
   * Runs in the capture phase on window, so it runs before the theme's own handlers.
   * @param {Event} event
   * @param {HTMLFormElement | null | undefined} form
   * @param {HTMLButtonElement | null} button
   */
  function intercept(event, form, button) {
    if (!form || form === passForm || button?.disabled) return;
    if (!getPreorderInfo(form)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    handlePreorderAdd(form, button);
  }

  window.addEventListener(
    'click',
    (event) => {
      const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
      const button = /** @type {HTMLButtonElement | null} */ (target?.closest('button'));
      if (!button || button.type !== 'submit' || !button.form) return;
      intercept(event, button.form, button);
    },
    true
  );

  window.addEventListener(
    'submit',
    (event) => {
      const form = /** @type {HTMLFormElement} */ (event.target);
      const submitter = /** @type {HTMLButtonElement | null} */ (/** @type {SubmitEvent} */ (event).submitter);

      if (submitter?.name === 'checkout' && cartIsMixed) {
        event.preventDefault();
        event.stopImmediatePropagation();
        applyCartRules();
        return;
      }

      intercept(event, form, submitter);
    },
    true
  );

  /* Cart rules: pre-orders and in-stock items check out separately */

  let cartIsMixed = false;
  let applyScheduled = false;

  function applyCartRules() {
    applyScheduled = false;

    document.querySelectorAll('button[name="checkout"]').forEach((element) => {
      const button = /** @type {HTMLButtonElement} */ (element);
      const container = button.parentElement;
      if (!container) return;

      let warning = container.querySelector(':scope > .preorder-cart-warning');

      if (cartIsMixed) {
        if (!warning) {
          warning = document.createElement('p');
          warning.className = 'preorder-cart-warning';
          warning.setAttribute('role', 'alert');
          warning.textContent = config.mixedCart;
          container.insertBefore(warning, button);
        }
        if (!button.disabled) button.disabled = true;
        button.dataset.preorderDisabled = 'true';
      } else {
        warning?.remove();
        if (button.dataset.preorderDisabled) {
          delete button.dataset.preorderDisabled;
          button.disabled = false;
        }
      }
    });

    document.querySelectorAll('.additional-checkout-buttons').forEach((element) => {
      const buttons = /** @type {HTMLElement} */ (element);
      const display = cartIsMixed ? 'none' : '';
      if (buttons.style.display !== display) buttons.style.display = display;
    });
  }

  function scheduleApply() {
    if (applyScheduled) return;
    applyScheduled = true;
    requestAnimationFrame(applyCartRules);
  }

  async function refreshCartState() {
    try {
      const cart = await fetchCart();
      const preorderCount = cart.items.filter(isPreorderItem).length;
      cartIsMixed = preorderCount > 0 && preorderCount < cart.items.length;
    } catch (error) {
      console.error('Could not check the cart', error);
      cartIsMixed = false;
    }
    applyCartRules();
  }

  // The theme re-renders the cart drawer and cart page after changes, so re-apply when the DOM changes.
  new MutationObserver(scheduleApply).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['disabled'],
  });

  document.addEventListener('cart:update', () => refreshCartState());
  refreshCartState();
})();
